import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Spin, Alert, Button, Space, Tooltip } from 'antd';
import {
    FullscreenOutlined,
    FullscreenExitOutlined,
    DisconnectOutlined,
    ReloadOutlined,
    ClearOutlined
} from '@ant-design/icons';
import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import 'xterm/css/xterm.css';

/**
 * Terminal Console Component using xterm.js
 *
 * @param {Object} props
 * @param {string} props.wsUrl - WebSocket URL (wss://host/terminal?vm=xxx&token=xxx&ts=xxx)
 * @param {Function} props.onConnect - Callback when connected
 * @param {Function} props.onDisconnect - Callback when disconnected
 * @param {Function} props.onError - Callback on error
 */
const TerminalConsole = ({
    wsUrl,
    onConnect,
    onDisconnect,
    onError,
    onReconnect, // fetch a fresh token; tokens expire after 5 min
    style = {}
}) => {
    const containerRef = useRef(null);
    const terminalRef = useRef(null);
    const fitAddonRef = useRef(null);
    const wsRef = useRef(null);
    const [status, setStatus] = useState('disconnected');
    const [errorMsg, setErrorMsg] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Initialize terminal
    const initTerminal = useCallback(() => {
        if (!containerRef.current || terminalRef.current) return;

        const terminal = new Terminal({
            cursorBlink: true,
            cursorStyle: 'block',
            fontSize: 14,
            fontFamily: '"Cascadia Code", "Fira Code", Menlo, Monaco, "Courier New", monospace',
            theme: {
                background: '#1e1e1e',
                foreground: '#d4d4d4',
                cursor: '#d4d4d4',
                selection: 'rgba(255, 255, 255, 0.3)',
                black: '#000000',
                red: '#cd3131',
                green: '#0dbc79',
                yellow: '#e5e510',
                blue: '#2472c8',
                magenta: '#bc3fbc',
                cyan: '#11a8cd',
                white: '#e5e5e5',
                brightBlack: '#666666',
                brightRed: '#f14c4c',
                brightGreen: '#23d18b',
                brightYellow: '#f5f543',
                brightBlue: '#3b8eea',
                brightMagenta: '#d670d6',
                brightCyan: '#29b8db',
                brightWhite: '#ffffff'
            },
            scrollback: 10000,
            convertEol: true,
        });

        const fitAddon = new FitAddon();
        const webLinksAddon = new WebLinksAddon();

        terminal.loadAddon(fitAddon);
        terminal.loadAddon(webLinksAddon);
        terminal.open(containerRef.current);

        // Fit after a short delay to ensure container has size
        setTimeout(() => fitAddon.fit(), 100);

        terminalRef.current = terminal;
        fitAddonRef.current = fitAddon;

        return terminal;
    }, []);

    // Connect WebSocket
    const connect = useCallback(() => {
        if (!wsUrl) return;

        setStatus('connecting');
        setErrorMsg('');

        // Initialize terminal if not done
        const terminal = terminalRef.current || initTerminal();
        if (!terminal) return;

        try {
            const ws = new WebSocket(wsUrl);
            ws.binaryType = 'arraybuffer';

            ws.onopen = () => {
                setStatus('connected');
                terminal.clear();
                terminal.writeln('\x1b[32mConnected to console\x1b[0m');
                terminal.writeln('Press Enter to activate...\r\n');

                // Send initial terminal size
                const { cols, rows } = terminal;
                ws.send(JSON.stringify({ type: 'resize', cols, rows }));

                onConnect?.();
            };

            ws.onmessage = (event) => {
                if (event.data instanceof ArrayBuffer) {
                    terminal.write(new Uint8Array(event.data));
                } else {
                    terminal.write(event.data);
                }
            };

            ws.onclose = (event) => {
                setStatus('disconnected');
                if (!event.wasClean) {
                    terminal.writeln('\r\n\x1b[31mConnection closed unexpectedly\x1b[0m');
                    setErrorMsg('Connection closed unexpectedly');
                    onError?.('Connection closed unexpectedly');
                } else {
                    terminal.writeln('\r\n\x1b[33mDisconnected\x1b[0m');
                    onDisconnect?.();
                }
            };

            ws.onerror = (error) => {
                setStatus('error');
                setErrorMsg('WebSocket error');
                terminal.writeln('\r\n\x1b[31mWebSocket error\x1b[0m');
                onError?.('WebSocket error');
            };

            // Handle terminal input
            terminal.onData((data) => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(data);
                }
            });

            // Handle terminal resize
            terminal.onResize(({ cols, rows }) => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'resize', cols, rows }));
                }
            });

            wsRef.current = ws;
        } catch (err) {
            setErrorMsg(err.message);
            setStatus('error');
            onError?.(err.message);
        }
    }, [wsUrl, initTerminal, onConnect, onDisconnect, onError]);

    // Disconnect
    const disconnect = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setStatus('disconnected');
    }, []);

    // Toggle fullscreen
    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current?.parentElement) return;
        const container = containerRef.current.parentElement.parentElement;

        if (!document.fullscreenElement) {
            container.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    }, []);

    // Clear terminal
    const clearTerminal = useCallback(() => {
        if (terminalRef.current) {
            terminalRef.current.clear();
        }
    }, []);

    // Auto-connect on mount
    useEffect(() => {
        if (wsUrl) {
            connect();
        }
        return () => {
            disconnect();
            if (terminalRef.current) {
                terminalRef.current.dispose();
                terminalRef.current = null;
            }
        };
    }, [wsUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            if (fitAddonRef.current) {
                fitAddonRef.current.fit();
            }
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('fullscreenchange', () => {
            setIsFullscreen(!!document.fullscreenElement);
            setTimeout(handleResize, 100);
        });

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', ...style }}>
            {/* Toolbar */}
            <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid #e8e8e8',
                background: '#fafafa',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Space>
                    <span style={{
                        color: status === 'connected' ? '#52c41a' :
                            status === 'connecting' ? '#faad14' : '#999',
                        fontWeight: 500
                    }}>
                        {status === 'connected' ? 'Connected' :
                            status === 'connecting' ? 'Connecting...' : 'Disconnected'}
                    </span>
                </Space>
                <Space>
                    <Tooltip title="Clear terminal">
                        <Button
                            size="small"
                            icon={<ClearOutlined />}
                            onClick={clearTerminal}
                            disabled={status !== 'connected'}
                        />
                    </Tooltip>
                    <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                        <Button
                            size="small"
                            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={toggleFullscreen}
                        />
                    </Tooltip>
                    {status === 'connected' ? (
                        <Tooltip title="Disconnect">
                            <Button
                                size="small"
                                danger
                                icon={<DisconnectOutlined />}
                                onClick={disconnect}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Reconnect">
                            <Button
                                size="small"
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={onReconnect || connect}
                                loading={status === 'connecting'}
                            />
                        </Tooltip>
                    )}
                </Space>
            </div>

            {/* Terminal Container */}
            <div style={{ flex: 1, position: 'relative', background: '#1e1e1e', minHeight: 400 }}>
                <div
                    ref={containerRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        padding: 8
                    }}
                />

                {status === 'connecting' && !terminalRef.current && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center'
                    }}>
                        <Spin size="large" />
                        <div style={{ color: '#fff', marginTop: 16 }}>Connecting to terminal...</div>
                    </div>
                )}

                {status === 'error' && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        maxWidth: 400
                    }}>
                        <Alert
                            type="error"
                            message="Connection Error"
                            description={errorMsg}
                            showIcon
                            action={
                                <Button size="small" onClick={onReconnect || connect}>
                                    Retry
                                </Button>
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TerminalConsole;
